import { useState, useEffect } from 'react'
import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui'
import { AttributeChipInput } from '@/components/admin/AttributeChipInput'
import { updateAsset } from '@/services/assets'
import type { ApprovedAsset } from '@/types/assets'

interface AssetEditDrawerProps {
  asset: ApprovedAsset | null
  onClose: () => void
  onSaved: () => void // page refetches + closes
}

/** First edit affordance on the Assets page: name, description, tags.
 *  Bytes and slug are immutable after upload (backend contract). url-kind
 *  assets additionally get an editable Link URL field (SA-48 Task 10) —
 *  the PUT body only gains `url` for those, matching the backend's
 *  kind-gated acceptance (ApprovedAssetUpdate). */
export function AssetEditDrawer({ asset, onClose, onSaved }: AssetEditDrawerProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [url, setUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!asset) return
    setName(asset.name)
    setDescription(asset.description ?? '')
    setTags(asset.tags)
    setUrl(asset.url ?? '')
    setError(false)
  }, [asset])

  if (!asset) return null

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    setError(false)
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
        tags,
      }
      if (asset.kind === 'url') {
        body.url = url.trim()
      }
      const res = await updateAsset(asset.id, body)
      if (!res.ok) {
        setError(true)
        return
      }
      onSaved()
    } catch {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose() }}>
      <DrawerContent className="max-w-md">
        <DrawerHeader>
          <DrawerTitle>Edit: {asset.name}</DrawerTitle>
        </DrawerHeader>

        <DrawerBody className="space-y-3">
          <div>
            <label htmlFor="asset-edit-name" className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
            <input id="asset-edit-name" value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background" />
          </div>
          <div>
            <label htmlFor="asset-edit-description" className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
            <textarea id="asset-edit-description" rows={2}
              value={description} onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background" />
            <p className="text-[11px] text-muted-foreground mt-0.5">
              The AI reads the description when deciding which file fits a request.
            </p>
          </div>
          <AttributeChipInput label="Tags" inputId="asset-edit-tags"
            values={tags} onChange={setTags}
            placeholder="e.g. brochure, menu — helps search and helps the AI pick the right file" />
          {asset.kind === 'url' && (
            <div>
              <label htmlFor="asset-edit-url" className="block text-xs font-medium text-muted-foreground mb-1">Link URL</label>
              <input id="asset-edit-url" value={url}
                onChange={e => setUrl(e.target.value)}
                className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background" />
            </div>
          )}
          <div>
            <p className="block text-xs font-medium text-muted-foreground mb-1">Slug (immutable)</p>
            <p className="text-sm font-mono text-muted-foreground">{asset.slug}</p>
          </div>
          {error && <p className="text-xs text-destructive">Couldn't save. Please try again.</p>}
        </DrawerBody>

        <DrawerFooter>
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-muted-foreground">Cancel</button>
          <button onClick={handleSave} disabled={saving || !name.trim()}
            className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
